/**
 * Attaching images to an `ai_dynamic_section` — the authoring half of ADR-0003. A thin wrapper over
 * `MediaUploadGrid`, adding only what is specific to a section: the grid speaks display URLs while
 * the pool stores storage keys, and each entry needs a stable `asset_id` the prober can name. No
 * author field for `description`/`relevance`: ADR-0003 leaves both reaching no prompt.
 */
import { useState } from 'react';
import MediaUploadGrid from '../../components/survey/MediaUploadGrid';
import {
  generateMediaAssetId,
  mediaAssetKeyFromUpload,
  questionImageSrcFromKey,
  type AiDynamicSectionMediaAsset,
} from './aiDynamicSection';

export interface AiDynamicSectionMediaPoolProps {
  mediaPool: AiDynamicSectionMediaAsset[];
  onChange: (mediaPool: AiDynamicSectionMediaAsset[]) => void;
  disabled?: boolean;
}

const LABEL_TEXT = 'Images the moderator can show';

/** Says what the images DO: not decoration on an authored question, but what the moderator looks
 *  at while deciding what to ask. */
const HELPER_TEXT =
  'The AI moderator can see these images and will ask the respondent about what is in them — a ' +
  'pack, an ad, a concept board. It decides for itself which one to show, and when.';

/** Every asset is re-sent on every turn (ADR-0003), so a ceiling belongs where the author sees it. */
const MAX_MEDIA_ASSETS = 6;

/** The grid also offers a URL field, and a link to somewhere else on the internet cannot become an
 *  `s3://` reference — stored, it would attach nothing and say nothing. */
const UNSUPPORTED_SOURCE_ERROR =
  'That link cannot be used here — the moderator reads the image out of our own storage. Upload ' +
  'the image file itself instead.';

const DUPLICATE_ASSET_ERROR =
  'That image is already attached. Adding it twice would let the moderator present the same ' +
  'picture as two different ones.';

/** A key that resolves to no serve URL still has to occupy its slot, or every index below it
 *  shifts and Remove takes the wrong asset. The grid's own broken-image fallback then shows. */
const srcForAsset = (asset: AiDynamicSectionMediaAsset): string =>
  questionImageSrcFromKey(asset.s3_key) ?? asset.s3_key;

export function AiDynamicSectionMediaPool({
  mediaPool,
  onChange,
  disabled,
}: AiDynamicSectionMediaPoolProps) {
  const [error, setError] = useState<string | null>(null);

  const addAsset = (url: string, path?: string) => {
    const s3Key = mediaAssetKeyFromUpload({ url, path });
    if (!s3Key) {
      setError(UNSUPPORTED_SOURCE_ERROR);
      return;
    }
    if (mediaPool.some((asset) => asset.s3_key.replace('s3://', '') === s3Key)) {
      setError(DUPLICATE_ASSET_ERROR);
      return;
    }
    setError(null);
    onChange([...mediaPool, { asset_id: generateMediaAssetId(), s3_key: s3Key }]);
  };

  const removeAsset = (index: number) => {
    setError(null);
    onChange(mediaPool.filter((_, i) => i !== index));
  };

  return (
    <div data-testid="ai-dynamic-section-media-pool">
      <span className="block text-sm font-medium text-gray-700 mb-1">{LABEL_TEXT}</span>
      <p className="text-xs text-gray-500 mb-2">{HELPER_TEXT}</p>
      <MediaUploadGrid
        mediaUrls={mediaPool.map(srcForAsset)}
        onMediaAdd={addAsset}
        onMediaRemove={removeAsset}
        maxItems={MAX_MEDIA_ASSETS}
        disabled={disabled}
        /* The prober attaches images only, so a clip would be named in the prompt and invisible. */
        allowVideo={false}
      />
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

export default AiDynamicSectionMediaPool;
